import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../components/common/ConfirmDialog";
import {
  Crown, Plus, Copy, Trash2, Ban, RefreshCw, Search,
  CheckCircle2, Clock, X as XIcon, AlertTriangle, Sparkles, Mail, Calendar, ExternalLink, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active:   { label: "Ativo",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",   icon: CheckCircle2 },
  redeemed: { label: "Resgatado",  cls: "bg-violet-50 text-violet-700 border-violet-200",      icon: Crown },
  expired:  { label: "Expirado",   cls: "bg-amber-50 text-amber-700 border-amber-200",         icon: Clock },
  revoked:  { label: "Revogado",   cls: "bg-rose-50 text-rose-600 border-rose-200",            icon: Ban },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  const Ico = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wider border ${cfg.cls}`}>
      <Ico size={10} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
}

export default function VipAccessAdminPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/vip-access/list`, { headers: authHeaders });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao carregar lista VIP");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleCopy = (text, msg = "Copiado!") => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  const handleRevoke = async (code) => {
    if (!(await confirmDialog({
      title: "Revogar código",
      message: `Revogar o código ${code}?\n\nIsso desabilita o link de convite. Quem JÁ resgatou continua com PRO normalmente.`,
      confirmLabel: "Revogar",
      variant: "danger",
    }))) return;
    try {
      await axios.post(`${API}/admin/vip-access/${code}/revoke`, {}, { headers: authHeaders });
      toast.success("Código revogado");
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao revogar");
    }
  };

  const handleDelete = async (code) => {
    if (!(await confirmDialog({
      title: "Apagar código",
      message: `Apagar permanentemente o código ${code}?\n\n(só funciona se NÃO foi resgatado)`,
      confirmLabel: "Apagar",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/admin/vip-access/${code}`, { headers: authHeaders });
      toast.success("Código apagado");
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao apagar");
    }
  };

  const filtered = items.filter((it) => {
    if (filterStatus !== "all" && it.status !== filterStatus) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (it.email || "").toLowerCase().includes(q) ||
      (it.name || "").toLowerCase().includes(q) ||
      (it.code || "").toLowerCase().includes(q) ||
      (it.note || "").toLowerCase().includes(q)
    );
  });

  // counters by status
  const counters = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="vip-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-1">
            <Crown size={18} className="text-amber-500" />
            <span className="text-[11px] font-extrabold text-amber-700 uppercase tracking-widest">Acessos VIP</span>
          </div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Convites Premium</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Libere PRO para influencers, parceiros e VIPs sem cobrar via Stripe</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchList} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all" data-testid="vip-refresh-btn">
            <RefreshCw size={14} />
            Atualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-[0_4px_14px_-4px_rgba(139,92,246,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(139,92,246,0.7)] transition-all font-medium" data-testid="vip-create-btn">
            <Plus size={15} />
            Novo VIP
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vip-stats">
        {[
          { key: "active", label: "Ativos", color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
          { key: "redeemed", label: "Resgatados", color: "text-violet-600", bg: "bg-violet-50 border-violet-100" },
          { key: "expired", label: "Expirados", color: "text-amber-600", bg: "bg-amber-50 border-amber-100" },
          { key: "revoked", label: "Revogados", color: "text-rose-500", bg: "bg-rose-50 border-rose-100" },
        ].map((c) => (
          <div key={c.key} className={`rounded-2xl border p-4 ${c.bg}`} data-testid={`vip-stat-${c.key}`}>
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-500">{c.label}</p>
            <p className={`text-[26px] font-bold tracking-tighter tabular-nums leading-tight ${c.color} mt-0.5`}>{counters[c.key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email, nome, código ou nota…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-white border border-zinc-200 text-[13px] text-zinc-900 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
            data-testid="vip-search-input"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {[
            { key: "all", label: "Todos" },
            { key: "active", label: "Ativos" },
            { key: "redeemed", label: "Resgatados" },
            { key: "expired", label: "Expirados" },
            { key: "revoked", label: "Revogados" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`shrink-0 px-3 h-10 rounded-lg text-[12.5px] font-medium transition-all ${filterStatus === f.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
              data-testid={`vip-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center" data-testid="vip-loading">
            <Loader2 size={28} className="animate-spin mx-auto text-violet-500" />
            <p className="text-[13px] text-zinc-500 mt-3">Carregando convites…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="vip-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <Sparkles size={24} className="text-zinc-400" />
            </div>
            <p className="text-[14px] font-semibold text-zinc-700">Nenhum convite VIP {search ? "encontrado" : "ainda"}</p>
            <p className="text-[12.5px] text-zinc-500 mt-1">
              {search ? "Tente outros termos de busca" : "Clique em 'Novo VIP' para criar o primeiro"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((it) => (
              <div key={it.code} className="p-4 sm:p-5 hover:bg-zinc-50 transition-colors" data-testid={`vip-row-${it.code}`}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Left: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-mono text-[13px] font-bold text-zinc-900 tracking-tight">{it.code}</span>
                      <StatusPill status={it.status} />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">{it.duration_days}d</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[13px] text-zinc-700">
                      <Mail size={12} className="text-zinc-400" />
                      <span className="truncate font-medium">{it.email}</span>
                      {it.name && <span className="text-zinc-400">· {it.name}</span>}
                    </div>
                    {it.note && (
                      <p className="text-[12px] text-zinc-500 italic mt-1 truncate">📝 {it.note}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-[11.5px] text-zinc-400">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} />
                        Criado {new Date(it.created_at).toLocaleDateString("pt-BR")}
                      </span>
                      {it.redeemed_at && (
                        <span className="inline-flex items-center gap-1 text-violet-600 font-medium">
                          <CheckCircle2 size={10} />
                          Resgatado {new Date(it.redeemed_at).toLocaleDateString("pt-BR")}
                        </span>
                      )}
                      {!it.redeemed_at && it.expires_at && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={10} />
                          Expira {new Date(it.expires_at).toLocaleDateString("pt-BR")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: actions */}
                  <div className="flex gap-1.5 flex-wrap sm:justify-end">
                    {it.status === "active" && (
                      <>
                        <button
                          onClick={() => handleCopy(it.invite_url, "Link copiado!")}
                          className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 text-[12px] font-medium transition-colors"
                          data-testid={`vip-copy-link-${it.code}`}
                          title="Copiar link de convite"
                        >
                          <Copy size={11} />
                          Link
                        </button>
                        <a
                          href={it.invite_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 text-[12px] font-medium transition-colors"
                          title="Abrir link em nova aba"
                        >
                          <ExternalLink size={11} />
                        </a>
                      </>
                    )}
                    <button
                      onClick={() => handleCopy(it.code, "Código copiado!")}
                      className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 text-[12px] font-medium transition-colors"
                      data-testid={`vip-copy-code-${it.code}`}
                      title="Copiar código"
                    >
                      <Copy size={11} />
                      Código
                    </button>
                    {it.status === "active" && (
                      <button
                        onClick={() => handleRevoke(it.code)}
                        className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 text-[12px] font-medium transition-colors"
                        data-testid={`vip-revoke-${it.code}`}
                        title="Revogar (desativa link mas mantém quem já resgatou)"
                      >
                        <Ban size={11} />
                        Revogar
                      </button>
                    )}
                    {!it.redeemed_at && (
                      <button
                        onClick={() => handleDelete(it.code)}
                        className="flex items-center gap-1.5 w-8 h-8 rounded-lg bg-zinc-100 text-zinc-400 hover:bg-rose-50 hover:text-rose-500 transition-colors"
                        data-testid={`vip-delete-${it.code}`}
                        title="Apagar registro"
                      >
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateVipModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchList(); }}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}

/* ── Create Modal ──────────────────────────────────────────────────────── */
function CreateVipModal({ onClose, onCreated, authHeaders }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(365);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdGrant, setCreatedGrant] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/vip-access/create`, {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        plan: "pro",
        duration_days: duration,
        note: note.trim() || null,
      }, { headers: authHeaders });
      setCreatedGrant(res.data);
      toast.success("Convite VIP criado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao criar");
      setSubmitting(false);
    }
  };

  const close = () => {
    if (createdGrant) onCreated(); else onClose();
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close} data-testid="vip-create-modal">
      <div className="bg-white w-full sm:max-w-[480px] rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ fontFamily: "'Poppins', sans-serif" }}>
        {!createdGrant ? (
          <>
            <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
              <div>
                <h2 className="text-[18px] font-semibold text-zinc-900 tracking-tight flex items-center gap-2">
                  <Crown size={18} className="text-amber-500" />
                  Novo Convite VIP
                </h2>
                <p className="text-[12.5px] text-zinc-500 mt-0.5">Libera PRO sem cobrança Stripe</p>
              </div>
              <button onClick={close} className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-500 hover:bg-zinc-200 flex items-center justify-center" data-testid="vip-modal-close">
                <XIcon size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4" data-testid="vip-create-form">
              <div>
                <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Email autorizado *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="mariana@email.com"
                  required
                  className="w-full h-11 px-3 rounded-lg bg-white border border-zinc-200 text-[13.5px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                  data-testid="vip-form-email"
                />
                <p className="text-[11px] text-zinc-400 mt-1">Apenas esse email poderá resgatar (intransferível)</p>
              </div>

              <div>
                <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Nome (opcional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Mariana Silva"
                  className="w-full h-11 px-3 rounded-lg bg-white border border-zinc-200 text-[13.5px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                  data-testid="vip-form-name"
                />
              </div>

              <div>
                <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Duração do plano</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[30, 90, 180, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      className={`h-10 rounded-lg text-[12.5px] font-medium transition-all ${duration === d ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                      data-testid={`vip-form-duration-${d}`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">PRO ativo por {duration} dias após resgate</p>
              </div>

              <div>
                <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Nota interna (opcional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder='Ex: "Influencer @mariana — parceria Q2"'
                  rows={2}
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-200 text-[13.5px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all resize-none"
                  data-testid="vip-form-note"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={close} className="flex-1 h-11 rounded-lg bg-zinc-100 text-zinc-700 text-[13px] font-medium hover:bg-zinc-200 transition-colors">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 h-11 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[13px] font-bold flex items-center justify-center gap-1.5 shadow-[0_4px_14px_-4px_rgba(139,92,246,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(139,92,246,0.7)] transition-all disabled:opacity-60"
                  data-testid="vip-form-submit"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />}
                  Gerar convite
                </button>
              </div>
            </form>
          </>
        ) : (
          /* ── Success state with copy invite link ────────────────────────── */
          <div className="p-6 sm:p-8" data-testid="vip-created-success">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-5 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.6)]">
              <CheckCircle2 size={28} className="text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-[20px] font-semibold text-center text-zinc-900 tracking-tight">Convite VIP pronto!</h2>
            <p className="text-[13px] text-center text-zinc-500 mt-1.5">Compartilhe o link abaixo com <span className="font-medium text-zinc-700">{createdGrant.email}</span></p>

            <div className="mt-5 p-3.5 bg-violet-50 border border-violet-200 rounded-xl">
              <p className="text-[10.5px] font-bold uppercase tracking-widest text-violet-700 mb-1">Link de Convite</p>
              <p className="text-[12.5px] font-mono text-zinc-900 break-all leading-relaxed">{createdGrant.invite_url}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                onClick={() => { navigator.clipboard.writeText(createdGrant.invite_url); toast.success("Link copiado!"); }}
                className="h-11 rounded-lg bg-violet-100 text-violet-700 text-[13px] font-bold flex items-center justify-center gap-1.5 hover:bg-violet-200 transition-colors"
                data-testid="vip-success-copy-link"
              >
                <Copy size={13} /> Copiar Link
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Oi! Você tem um convite VIP Liventy esperando 🎁\n\n${createdGrant.invite_url}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 rounded-lg bg-emerald-100 text-emerald-700 text-[13px] font-bold flex items-center justify-center gap-1.5 hover:bg-emerald-200 transition-colors"
                data-testid="vip-success-whatsapp"
              >
                <Sparkles size={13} /> WhatsApp
              </a>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2.5">
              <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[11.5px] text-amber-800 leading-relaxed">
                <span className="font-bold">Atenção:</span> apenas <span className="font-mono font-bold">{createdGrant.email}</span> poderá usar este link. Outros emails serão bloqueados.
              </p>
            </div>

            <button onClick={close} className="w-full h-11 mt-4 rounded-lg bg-zinc-900 text-white text-[13px] font-bold hover:bg-zinc-800 transition-colors" data-testid="vip-success-done">
              Concluir
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
